import React, { useState, useEffect, useMemo } from 'react';

export default function DriverLogins({ user, driverId, onBack }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [driverName, setDriverName] = useState('');
    const [filterDate, setFilterDate] = useState('');
    const [expandedDate, setExpandedDate] = useState('');

    useEffect(() => {
        if (!driverId) return;
        setLoading(true);
        fetch(`/api/drivers/${driverId}/logs`)
            .then(res => res.json())
            .then(data => {
                setLogs(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));

        // Let's also try to get the driver name from a general drivers fetch, or just rely on logs.
        // Actually, if we fetch the single driver:
        fetch(`/api/drivers/all`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    const d = data.find(drv => drv._id === driverId);
                    if (d) setDriverName(d.name);
                }
            })
            .catch(() => {});
    }, [driverId]);

    const filteredLogs = useMemo(() => {
        if (!filterDate) return logs;
        return logs.filter(log => log.date === filterDate);
    }, [logs, filterDate]);

    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <path d={d} />
        </svg>
    );

    const icons = {
        back: 'M15 18l-6-6 6-6',
        search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
        calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
    };

    return (
        <div className="driver-logins-page">
            <style>{`
                .driver-logins-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .theme-dark .driver-logins-page { background: #0f172a; }

                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .title-area { display: flex; align-items: center; gap: 16px; }
                
                .back-btn {
                    background: white; border: 1px solid #e2e8f0; border-radius: 10px;
                    width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
                    cursor: pointer; color: #64748b; transition: all 0.2s;
                }
                .theme-dark .back-btn { background: #1e293b; border-color: #334155; color: #94a3b8; }
                .back-btn:hover { background: #f1f5f9; color: #1e293b; }
                .theme-dark .back-btn:hover { background: #334155; color: #f1f5f9; }

                .title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .theme-dark .title { color: #f1f5f9; }

                .toolbar { 
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; 
                    background: white; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0;
                }
                .theme-dark .toolbar { background: #1e293b; border-color: #334155; }

                .filter-group { display: flex; align-items: center; gap: 12px; }
                .filter-label { font-size: 13px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                .theme-dark .filter-label { color: #94a3b8; }

                .date-input {
                    padding: 10px 14px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px;
                    color: #1e293b; outline: none; transition: border-color 0.2s; background: #f8fafc;
                }
                .theme-dark .date-input { background: #0f172a; border-color: #334155; color: white; }
                .date-input:focus { border-color: #00B2A9; }

                .table-container { 
                    background: white; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }
                .theme-dark .table-container { background: #1e293b; border-color: #334155; }

                .logs-table { width: 100%; border-collapse: collapse; }
                .logs-table th { 
                    background: #f8fafc; padding: 16px 20px; text-align: left; font-size: 12px; 
                    font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;
                    border-bottom: 1px solid #e2e8f0;
                }
                .theme-dark .logs-table th { background: #1a2233; color: #94a3b8; border-bottom-color: #334155; }

                .logs-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; }
                .theme-dark .logs-table td { border-bottom-color: #334155; color: #cbd5e1; }
                .logs-table tr:last-child td { border-bottom: none; }
                .logs-table tr:hover { background: #fdfdff; }
                .theme-dark .logs-table tr:hover { background: #242f45; }

                .date-link {
                    background: none; border: none; padding: 0; color: #11427D; font-weight: 700;
                    cursor: pointer; display: inline-flex; align-items: center; gap: 8px; font: inherit;
                }
                .theme-dark .date-link { color: #7dd3fc; }
                .date-link:hover { text-decoration: underline; }
                .expand-icon { font-size: 12px; color: #64748b; }
                .theme-dark .expand-icon { color: #94a3b8; }
                .trip-details-row td { background: #f8fafc; padding: 0; }
                .theme-dark .trip-details-row td { background: #111827; }
                .trip-details { padding: 16px 20px 18px 52px; border-bottom: 1px solid #e2e8f0; }
                .theme-dark .trip-details { border-bottom-color: #334155; }
                .trip-details-title { font-size: 13px; font-weight: 800; color: #334155; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.04em; }
                .theme-dark .trip-details-title { color: #cbd5e1; }
                .trip-details-table { width: 100%; border-collapse: collapse; background: white; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
                .theme-dark .trip-details-table { background: #1e293b; border-color: #334155; }
                .trip-details-table th, .trip-details-table td { padding: 12px 14px; border-bottom: 1px solid #edf2f7; font-size: 13px; }
                .theme-dark .trip-details-table th, .theme-dark .trip-details-table td { border-bottom-color: #334155; }
                .trip-details-table th { color: #64748b; background: #f8fafc; text-transform: uppercase; font-size: 11px; letter-spacing: 0.04em; }
                .theme-dark .trip-details-table th { color: #94a3b8; background: #1a2233; }
                .trip-details-table tr:last-child td { border-bottom: none; }
                .no-trips { color: #64748b; font-size: 14px; }
                .theme-dark .no-trips { color: #94a3b8; }
                .empty-state { text-align: center; padding: 60px 20px; color: #64748b; }
            `}</style>

            <div className="header">
                <div className="title-area">
                    <button className="back-btn" onClick={onBack}>
                        <Icon d={icons.back} size={20} />
                    </button>
                    <h1 className="title">Daily Logins {driverName ? `- ${driverName}` : ''}</h1>
                </div>
            </div>

            <div className="toolbar">
                <div className="filter-group">
                    <span className="filter-label">Filter By Date:</span>
                    <input 
                        type="date" 
                        className="date-input" 
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                    />
                    {filterDate && (
                        <button 
                            className="back-btn" 
                            style={{ height: '40px', width: 'auto', padding: '0 12px', fontSize: '13px', fontWeight: 600 }}
                            onClick={() => setFilterDate('')}
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            <div className="table-container">
                <table className="logs-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Vehicle</th>
                            <th>Start Odometer</th>
                            <th>End Odometer</th>
                            <th>Total Distance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="empty-state">Loading logs...</td></tr>
                        ) : filteredLogs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-state">
                                    <Icon d={icons.calendar} size={32} color="#cbd5e1" strokeWidth={1.5} />
                                    <div style={{ marginTop: '12px', fontSize: '15px' }}>
                                        {filterDate ? 'No logs found for the selected date.' : 'No daily logs found for this driver.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredLogs.map(log => {
                                const start = log.startOdometer || 0;
                                const end = log.endOdometer || 0;
                                const distance = end >= start ? end - start : 0;
                                const isExpanded = expandedDate === log.date;
                                const trips = Array.isArray(log.trips) ? log.trips : [];
                                
                                return (
                                    <React.Fragment key={log._id}>
                                        <tr>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="date-link"
                                                    onClick={() => setExpandedDate(isExpanded ? '' : log.date)}
                                                >
                                                    <span className="expand-icon">{isExpanded ? 'v' : '>'}</span>
                                                    {log.date}
                                                </button>
                                            </td>
                                            <td>
                                                {log.vehicleId ? (
                                                    <span style={{ fontWeight: 600, color: '#0f172a' }}>
                                                        {log.vehicleId.plateNumber}
                                                        {log.vehicleId.vehicleCode && <span style={{ marginLeft: 8, color: '#2563eb', fontWeight: 700, fontSize: 12 }}>{log.vehicleId.vehicleCode}</span>}
                                                    </span>
                                                ) : <span style={{ color: '#94a3b8' }}>—</span>}
                                            </td>
                                            <td style={{ fontWeight: 500 }}>{start} km</td>
                                            <td style={{ fontWeight: 500 }}>{end} km</td>
                                            <td>
                                                <span style={{ 
                                                    background: '#eef2ff', color: '#4f46e5', padding: '4px 10px', 
                                                    borderRadius: '6px', fontWeight: 700, fontSize: '13px' 
                                                }}>
                                                    {distance} km
                                                </span>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="trip-details-row">
                                                <td colSpan="5">
                                                    <div className="trip-details">
                                                        <div className="trip-details-title">Trips Attended</div>
                                                        {trips.length === 0 ? (
                                                            <div className="no-trips">No trips found for this date.</div>
                                                        ) : (
                                                            <table className="trip-details-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Trip Name</th>
                                                                        <th>Start Odometer</th>
                                                                        <th>End Odometer</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {trips.map((trip, index) => (
                                                                        <tr key={trip.instanceId || trip.tripId || index}>
                                                                            <td style={{ fontWeight: 700, color: '#11427D' }}>{trip.tripName || 'Unnamed Trip'}</td>
                                                                            <td>{trip.startOdometer ?? 0} km</td>
                                                                            <td>{trip.endOdometer ?? 0} km</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
